import { useTranslation } from 'react-i18next'

import { ProgressBar } from '../ProgressBar/ProgressBar'
import { ErrorCard } from '../ErrorCard/ErrorCard'

import { Flex, Button, ButtonText, Icon, Typography } from '@percent/lemonade'
import { StepProps } from './Step.types'
import styles from './Step.module.scss'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { FormState } from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'
import { useTrackEvent } from '@percent/education-hosted-form/analytics/useTrackEvent'

export function Step({
  title,
  description,
  step,
  onBack,
  onNext,
  nextButtonLabel,
  nextButtonDisabled,
  isLoading,
  children,
  progress
}: StepProps) {
  const { formState } = useEducationHostedForm()
  const { t } = useTranslation()
  const trackEvent = useTrackEvent()

  const handleNext = () => {
    // `step` is optional (e.g. ResultPage renders Step without one); only emit
    // attributed events when it is present.
    if (step) trackEvent('education_form_button_clicked', { button: 'next', step })
    onNext?.()
  }

  const handleBack = () => {
    if (step) trackEvent('education_form_button_clicked', { button: 'back', step })
    onBack?.()
  }

  if (formState === FormState.ERROR) {
    return (
      <ErrorCard
        title={t('errorMessage.somethingWentWrong')}
        subtitle={t('errorMessage.reloadPage')}
        variant={ErrorCard.variants.INTERNAL}
      />
    )
  }

  return (
    <>
      {onBack && (
        <ButtonText
          variant="primary"
          size="sm"
          onClick={handleBack}
          className={styles.backButton}
          data-testid="step-back-button"
        >
          <Icon name="arrow-left" size="m" />
        </ButtonText>
      )}
      <ProgressBar progress={progress} />
      <Flex className={styles.step} key={title}>
        {title && (
          <Typography variant="h2" className={styles.title}>
            {title}
          </Typography>
        )}
        {description && <Typography className={styles.description}>{description}</Typography>}
        {children}
        {onNext && nextButtonLabel && (
          <div className={styles.buttonWrapper}>
            <Button
              onClick={handleNext}
              data-testid="step-next-button"
              loading={isLoading}
              stretch
              disabled={isLoading || nextButtonDisabled}
            >
              {nextButtonLabel}
            </Button>
          </div>
        )}
      </Flex>
    </>
  )
}
